import { motion, type Variants } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { cn } from '@/utils/tailwind.util';
import { type AiBuilderPageSurfaceMode } from '@/views/build/features/ai-builder-chat/components/AiBuilderShellTransition';
import { AiGenerateDockBackgroundLock } from '@/views/build/features/ai-builder-chat/components/AiGenerateDockBackgroundLock';
import { AiGenerateDockMount } from '@/views/build/features/ai-builder-chat/components/AiGenerateDockMount';
import { useAiBuilderChatAvailability } from '@/views/build/features/ai-builder-chat/hooks/useAiBuilderChatAvailability';
import { useResetAiGenerateDock } from '@/views/build/features/ai-builder-chat/hooks/useResetAiGenerateDock';
import { aiGenerateDockRunningCountAtom, aiGenerateDockSlotAtom } from '@/views/build/features/ai-builder-chat/stores/aiGenerateDockRun.atoms';
import { QuizBankEntryCard, QuizBankEntryImpression, useQuizBankEntry } from '@/views/build/features/quiz-bank';
import { BuildAiEntryCards } from './BuildAiEntryCards';
import { BuildQuizButtons } from './BuildQuizButtons';
import { BuildQuizFloatingButtons } from './BuildQuizFloatingButtons';
import { QuizList } from './QuizList';
import { QuizSliderSidebar } from './QuizSlider';
import { useQuizList } from './useQuizList';
type BuildStepQuizProps = {
  /**
   * 현재 step이 'quiz'인지 여부.
   * false일 때는 display:none으로 화면에서만 숨기고 mount는 유지한다.
   * 이로써 카드 N개의 옵저버/구독/RHF 바인딩이 step 토글에도 살아남는다.
   */
  active: boolean;
  surfaceMode?: AiBuilderPageSurfaceMode;
};
export const BuildStepQuiz = ({
  active,
  surfaceMode = 'page'
}: BuildStepQuizProps) => {
  return (
    // display:none → IntersectionObserver / ResizeObserver / layout이 자동 비활성화.
    // 접근성/탭 포커스도 display:none이 자동으로 제거하므로 inert는 불필요.
    <div aria-hidden={!active} style={{
      display: active ? 'block' : 'none'
    }} data-sentry-component="BuildStepQuiz" data-sentry-source-file="BuildStepQuiz.tsx">
      <BuildStepQuizDefaultContent active={active} surfaceMode={surfaceMode} data-sentry-element="BuildStepQuizDefaultContent" data-sentry-source-file="BuildStepQuiz.tsx" />
    </div>
  );
};

/** AI 진입 카드·생성기를 보일지와 빈 퀴즈 배치를 한곳에서 정한다. */
const useBuildStepQuizAiEntry = ({
  active,
  surfaceMode
}: {
  active: boolean;
  surfaceMode: AiBuilderPageSurfaceMode;
}) => {
  // AI 진입 카드·생성기는 AI 를 쓸 수 있는 사용자(LTI 강사·교사 등)에게만 보인다.
  const {
    canUseAiBuilderChat
  } = useAiBuilderChatAvailability();
  // 생성 중엔 진입 카드를 숨긴다 — 완료·실패·중단 뒤 다시 보인다(PRD 3-5).
  const isGenerating = useAtomValue(aiGenerateDockRunningCountAtom) !== null;
  const {
    quizList
  } = useQuizList();
  const generatorSlot = useAtomValue(aiGenerateDockSlotAtom);
  return {
    showsGenerateDock: active && surfaceMode === 'page' && canUseAiBuilderChat,
    showsAiEntry: canUseAiBuilderChat && !isGenerating,
    // Figma 1-1 빈 퀴즈 — AI 진입 카드와 「문제 추가」 버튼을 화면 세로 가운데에 32 간격으로 둔다(PC 페이지만).
    // 생성기 대기·실패 카드가 들어올 자리가 있으면 빈 퀴즈가 아니다.
    isEmptyPageLayout: surfaceMode === 'page' && quizList.length === 0 && !generatorSlot
  };
};
const BuildStepQuizDefaultContent = ({
  active,
  surfaceMode
}: {
  active: boolean;
  surfaceMode: AiBuilderPageSurfaceMode;
}) => {
  const isPageRestoring = surfaceMode === 'page-restoring';
  const usesAiShellLayout = surfaceMode !== 'page';
  // 진입 카드 자리(AI 생성 카드·문제 은행 카드)는 페이지 화면에서만 — 채팅 패널이 열리면(AI 셸) 접는다.
  const shouldShowEntryArea = surfaceMode === 'page' || isPageRestoring;
  const {
    showsGenerateDock,
    showsAiEntry,
    isEmptyPageLayout
  } = useBuildStepQuizAiEntry({
    active,
    surfaceMode
  });
  // 문제 은행은 AI 기능이 아니다 — AI 사용 가능 여부·생성 중과 무관하게 노출 과목이면 보인다.
  const quizBank = useQuizBankEntry();
  const {
    quizList
  } = useQuizList();
  const showsQuizBankEntry = shouldShowEntryArea && quizBank.status === 'available';
  // 빌드 화면을 벗어나면 생성기 상태(실패 자리·열림·탭)를 비워 다음 편집 화면에 남지 않게 한다.
  useResetAiGenerateDock();
  return <>
      {/* 생성기 입력창이 펼쳐진 동안 뒤 화면(사이드바·목록)의 스크롤·클릭·포커스를 막는다. */}
      <AiGenerateDockBackgroundLock enabled={active && surfaceMode === 'page'} data-sentry-element="AiGenerateDockBackgroundLock" data-sentry-source-file="BuildStepQuiz.tsx">
        {surfaceMode === 'page' && <QuizSliderSidebar transitionState="visible" />}

        <motion.div key={'quiz'}
      // 항상 mount 상태이므로 AnimatePresence enter/exit 대신 active 변화에 따라
      // 직접 opacity + x를 애니메이트한다.
      // 기존 동작 재현: active=true 진입 시 x:-20 → 0으로 슬라이드 인.
      // initial={false}로 첫 mount에서 깜빡임을 막는다.
      initial={false} animate={{
        opacity: active ? 1 : 0,
        x: active ? 0 : -20
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} style={{
        pointerEvents: 'auto',
        position: 'relative'
      }} data-sentry-element="unknown" data-sentry-source-file="BuildStepQuiz.tsx">
          <div className={cn('mx-auto flex w-full flex-col', usesAiShellLayout ? 'max-w-[650px]' : 'max-w-[1034px] items-center gap-lg px-md mobile-lg:px-lg tablet-lg:flex-row tablet-lg:items-start tablet-lg:justify-center tablet-lg:px-0')}>
            <div className={cn('flex w-full max-w-[650px] flex-none flex-col', isEmptyPageLayout && EMPTY_PAGE_COLUMN_CLASS)}>
              {/* 진입 카드 자리 — AI 생성 카드와 문제 은행 카드가 같은 위계로 놓인다. */}
              <div className={cn('flex flex-col', (showsAiEntry || showsQuizBankEntry) && 'mb-lg', isEmptyPageLayout && 'tablet-lg:mb-0')}>
                <motion.div data-ai-builder-entry-wrapper aria-hidden={!shouldShowEntryArea} initial={false} animate={shouldShowEntryArea ? 'visible' : 'hidden'} variants={AI_ENTRY_WRAPPER_VARIANTS} style={{
                pointerEvents: shouldShowEntryArea ? 'auto' : 'none'
              }} data-sentry-element="unknown" data-sentry-source-file="BuildStepQuiz.tsx">
                  {showsAiEntry && <BuildAiEntryCards layoutMode={usesAiShellLayout ? 'ai-shell' : 'page'} hasQuizBankEntry={showsQuizBankEntry} />}
                </motion.div>
                {showsQuizBankEntry && <QuizBankEntryImpression
              // AI 카드가 있으면 그 아래 8 — 진입 카드끼리 같은 간격(카드 그리드 gap-y-xs)이다.
              className={cn(showsAiEntry && 'mt-xs')} isFull={quizBank.isFull} remainingSlots={quizBank.remainingSlots}>
                    <QuizBankEntryCard size={quizList.length === 0 ? 'large' : 'small'} onOpen={() => quizBank.open('build_entry_row')} />
                  </QuizBankEntryImpression>}
              </div>

              <QuizList
            // Figma 1-1: AI 진입 카드가 있는 빈 퀴즈엔 「또는 직접 문제를 만들어 보세요」 줄이 없다.
            showEmptyDescription={!(isEmptyPageLayout && showsAiEntry)} emptyDescriptionTransitionState={shouldShowEntryArea ? 'visible' : 'hidden'} data-sentry-element="QuizList" data-sentry-source-file="BuildStepQuiz.tsx" />
              <BuildQuizButtons layoutMode={usesAiShellLayout ? 'ai-shell' : 'page'} data-sentry-element="BuildQuizButtons" data-sentry-source-file="BuildStepQuiz.tsx" />
              {surfaceMode === 'page' && <BuildQuizFloatingButtons />}
            </div>
          </div>
        </motion.div>
      </AiGenerateDockBackgroundLock>

      {/* 채팅 패널이 열리면(AI 셸) 숨기기만 한다 — 생성기와 채팅은 서로 다른 도구다. */}
      <AiGenerateDockMount visible={showsGenerateDock} data-sentry-element="AiGenerateDockMount" data-sentry-source-file="BuildStepQuiz.tsx" />
    </>;
};

/**
 * 빈 퀴즈 가운데 열(PC) — 본문 위 여백(88)·아래 여백(3xl)을 뺀 높이에서 세로 가운데, 안쪽 위 24 · 아래 120,
 * 블록 사이 32 (Figma 1-1 Quiz 영역).
 */
const EMPTY_PAGE_COLUMN_CLASS = 'tablet-lg:min-h-[calc(100dvh-88px-theme(spacing.3xl))] tablet-lg:justify-center tablet-lg:gap-xl tablet-lg:pb-[120px] tablet-lg:pt-lg';
const AI_ENTRY_WRAPPER_VARIANTS: Variants = {
  hidden: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
    y: -8,
    transition: {
      duration: 0.22,
      ease: 'easeInOut'
    },
    transitionEnd: {
      visibility: 'hidden'
    }
  },
  visible: {
    height: 'auto',
    opacity: 1,
    overflow: 'hidden',
    visibility: 'visible',
    y: 0,
    transition: {
      duration: 0.24,
      ease: 'easeOut'
    },
    transitionEnd: {
      overflow: 'visible'
    }
  }
};